import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type {
  BusinessType,
  BookingSystem,
  PlaceDetails,
  BookingDetectionResult,
  LeadDataResponse,
  SaveBusinessTypeRequest,
  SavePlaceDetailsRequest,
  SaveEmailRequest,
  SaveContactRequest,
  SaveLanguagesRequest,
  SaveBookingSystemRequest,
  SaveBillingRequest,
  StepKey,
} from '@/types/onboarding'
import { BOOKING_SYSTEMS, buildBookingSystemPayload, ONBOARDING_STEPS, STEP } from '@/types/onboarding'

/** Infer the current onboarding step from populated data fields. */
export function inferStepFromData(
  data: LeadDataResponse,
  phoneVerified: boolean,
): StepKey {
  if (!data.business_type) return STEP.BUSINESS_TYPE
  if (!data.business_name && !data.business_address) return STEP.PLACE_SEARCH
  if (!data.email) return STEP.EMAIL
  if (!data.contact_first_name || !data.contact_last_name) return STEP.NAME
  if (!phoneVerified) return STEP.PHONE
  if (!data.primary_language) return STEP.LANGUAGES
  if (data.business_type === 'RESTAURANT' && !data.booking_system) return STEP.BOOKING_SYSTEM
  return STEP.PAYMENT
}

type StepSaveData =
  | SaveBusinessTypeRequest
  | SavePlaceDetailsRequest
  | SaveEmailRequest
  | SaveContactRequest
  | SaveLanguagesRequest
  | SaveBookingSystemRequest
  | SaveBillingRequest

interface OnboardingState {
  // Lead tracking
  leadId: string | null
  currentStep: StepKey

  // Flow control
  isManualEntry: boolean
  bookingDetectionStarted: boolean

  // Collected data
  businessType: BusinessType | null
  placeDetails: PlaceDetails | null
  email: string
  firstName: string
  lastName: string
  phoneNumber: string
  phoneVerified: boolean
  primaryLanguage: string
  additionalLanguages: string[]
  bookingSystem: BookingSystem | null
  restaurantId: string
  apiKey: string
  bookingValidated: boolean
  bookingDetection: BookingDetectionResult | null

  // Payment
  stripeClientSecret: string | null
  billingCountry: string | null
  paymentToken: string | null

  // UI State
  isLoading: boolean
  error: string | null

  // Actions
  setLeadId: (leadId: string) => void
  setCurrentStep: (step: StepKey) => void
  goToStep: (step: StepKey) => void

  setManualEntry: (isManual: boolean) => void
  setBookingDetectionStarted: (started: boolean) => void
  setBusinessType: (type: BusinessType) => void
  setPlaceDetails: (details: PlaceDetails | null) => void
  setEmail: (email: string) => void
  setName: (firstName: string, lastName: string) => void
  setPhoneNumber: (phone: string) => void
  setPhoneVerified: (verified: boolean) => void
  setLanguages: (primary: string, additional: string[]) => void
  setBookingSystem: (system: BookingSystem | null) => void
  setBookingCredentials: (restaurantId: string, apiKey?: string) => void
  setBookingValidated: (validated: boolean) => void
  setBookingDetection: (detection: BookingDetectionResult | null) => void
  setStripeClientSecret: (secret: string | null) => void
  setBillingCountry: (country: string | null) => void
  setPaymentToken: (paymentToken: string | null) => void

  setLoading: (loading: boolean) => void
  setError: (error: string | null) => void

  // Get step data for API
  getStepData: (step: StepKey) => StepSaveData | Record<string, never>

  // Check if a step is complete
  isStepComplete: (step: StepKey) => boolean

  // Hydrate from API response
  hydrateFromLead: (lead: {
    lead_id: string
    status: string
    data: LeadDataResponse
    phone_verified: boolean
    booking_validated: boolean
  }) => void

  // Reset
  reset: () => void
}

const initialState = {
  leadId: null,
  currentStep: STEP.BUSINESS_TYPE as StepKey,
  isManualEntry: false,
  bookingDetectionStarted: false,
  businessType: null,
  placeDetails: null,
  email: '',
  firstName: '',
  lastName: '',
  phoneNumber: '',
  phoneVerified: false,
  primaryLanguage: '',
  additionalLanguages: [] as string[],
  bookingSystem: null,
  restaurantId: '',
  apiKey: '',
  bookingValidated: false,
  bookingDetection: null,
  stripeClientSecret: null,
  billingCountry: null,
  paymentToken: null,
  isLoading: false,
  error: null,
}

export const useOnboardingStore = create<OnboardingState>()(
  persist(
    (set, get) => ({
      ...initialState,

      setLeadId: (leadId) => set({ leadId }),
      setCurrentStep: (step) => set({ currentStep: step }),
      goToStep: (step) => set({ currentStep: step }),

      setManualEntry: (isManual) => set({ isManualEntry: isManual }),
      setBookingDetectionStarted: (started) => set({ bookingDetectionStarted: started }),
      setBusinessType: (type) => set({ businessType: type }),
      setPlaceDetails: (details) => set({ placeDetails: details }),
      setEmail: (email) => set({ email }),
      setName: (firstName, lastName) => set({ firstName, lastName }),
      setPhoneNumber: (phone) => set({ phoneNumber: phone }),
      setPhoneVerified: (verified) => set({ phoneVerified: verified }),
      setLanguages: (primary, additional) => set({
        primaryLanguage: primary,
        additionalLanguages: additional
      }),
      setBookingSystem: (system) => set({ bookingSystem: system }),
      setBookingCredentials: (restaurantId, apiKey = '') => set({
        restaurantId,
        apiKey
      }),
      setBookingValidated: (validated) => set({ bookingValidated: validated }),
      setBookingDetection: (detection) => set({ bookingDetection: detection }),
      setStripeClientSecret: (secret) => set({ stripeClientSecret: secret }),
      setBillingCountry: (country) => set({ billingCountry: country }),
      setPaymentToken: (paymentToken) => set({ paymentToken }),

      setLoading: (loading) => set({ isLoading: loading }),
      setError: (error) => set({ error }),

      getStepData: (step) => {
        const state = get()
        switch (step) {
          case STEP.BUSINESS_TYPE:
            return { business_type: state.businessType ?? undefined } as SaveBusinessTypeRequest
          case STEP.PLACE_CONFIRM:
            return state.placeDetails ? {
              business_name: state.placeDetails.name,
              business_address: state.placeDetails.address,
              business_phone: state.placeDetails.phone,
              website: state.placeDetails.website,
              google_place_id: state.placeDetails.google_place_id,
              use_same_phone_for_events: state.placeDetails.useSamePhoneForEvents ?? false,
            } as SavePlaceDetailsRequest : {}
          case STEP.EMAIL:
            return { email: state.email } as SaveEmailRequest
          case STEP.NAME:
            return {
              contact_first_name: state.firstName,
              contact_last_name: state.lastName
            } as SaveContactRequest
          case STEP.LANGUAGES:
            return {
              primary_language: state.primaryLanguage,
              additional_languages: state.additionalLanguages,
            } as SaveLanguagesRequest
          case STEP.BOOKING_SYSTEM:
            return buildBookingSystemPayload(
              state.bookingSystem ?? '',
              state.restaurantId,
              state.apiKey,
            )
          case STEP.PAYMENT:
            return {} as SaveBillingRequest
          default:
            return {}
        }
      },

      isStepComplete: (step) => {
        const state = get()
        switch (step) {
          case STEP.BUSINESS_TYPE:
            return !!state.businessType
          case STEP.PLACE_SEARCH:
            return !!state.placeDetails || state.isManualEntry
          case STEP.PLACE_CONFIRM:
            return !!(state.placeDetails?.name && state.placeDetails?.address)
          case STEP.EMAIL:
            return !!state.email && state.email.includes('@')
          case STEP.NAME:
            return !!state.firstName && !!state.lastName
          case STEP.PHONE:
            return !!state.phoneNumber && state.phoneVerified
          case STEP.LANGUAGES:
            return !!state.primaryLanguage
          case STEP.BOOKING_SYSTEM:
            if (state.businessType !== 'RESTAURANT') {
              return true
            }
            return state.bookingValidated || state.bookingSystem === 'other'
          case STEP.PAYMENT:
            return true
          default:
            return false
        }
      },

      hydrateFromLead: (lead) => {
        const { data } = lead

        const bookingDetection: BookingDetectionResult | null = data.booking_detection_status
          ? {
              status: data.booking_detection_status as BookingDetectionResult['status'],
              detected_system: (data.detected_booking_system as BookingDetectionResult['detected_system']) || null,
              restaurant_id: data.detected_restaurant_id || null,
            }
          : null

        const inferredStep = inferStepFromData(data, lead.phone_verified)
        const restaurantId = data.booking_restaurant_id || data.booking_partner_id || ''

        // Batch all state into a single set() to avoid intermediate renders
        set({
          leadId: lead.lead_id,
          currentStep: inferredStep,
          phoneVerified: lead.phone_verified,
          bookingValidated: lead.booking_validated,
          bookingDetection,
          businessType: (data.business_type as BusinessType) ?? null,
          placeDetails: (data.business_name || data.business_address)
            ? {
                name: data.business_name || '',
                address: data.business_address || '',
                phone: data.business_phone || '',
                website: data.website || '',
                google_place_id: data.google_place_id || '',
                useSamePhoneForEvents: data.use_same_phone_for_events ?? false,
              }
            : null,
          email: data.email || '',
          billingCountry: data.billing_country || null,
          firstName: data.contact_first_name || '',
          lastName: data.contact_last_name || '',
          phoneNumber: data.phone_number || '',
          primaryLanguage: data.primary_language || '',
          additionalLanguages: data.additional_languages ?? [],
          bookingSystem: (data.booking_system as BookingSystem) ?? null,
          restaurantId,
          apiKey: data.booking_api_key ?? '',
        })
      },

      reset: () => set(initialState),
    }),
    {
      name: 'bonnie-onboarding',
      version: 1,
      migrate: (persisted: any, version: number) => {
        if (version === 0 && typeof persisted.currentStep === 'number') {
          const step = ONBOARDING_STEPS[persisted.currentStep - 1]
          persisted.currentStep = step?.key ?? STEP.BUSINESS_TYPE
        }
        return persisted
      },
      partialize: (state) => ({
        leadId: state.leadId,
        currentStep: state.currentStep,
        isManualEntry: state.isManualEntry,
        bookingDetectionStarted: state.bookingDetectionStarted,
        businessType: state.businessType,
        placeDetails: state.placeDetails,
        email: state.email,
        firstName: state.firstName,
        lastName: state.lastName,
        phoneNumber: state.phoneNumber,
        phoneVerified: state.phoneVerified,
        primaryLanguage: state.primaryLanguage,
        additionalLanguages: state.additionalLanguages,
        bookingSystem: state.bookingSystem,
        restaurantId: state.restaurantId,
        bookingValidated: state.bookingValidated,
        bookingDetection: state.bookingDetection,
      }),
    }
  )
)
